import { ORGANIZATION_TYPES, VENDORS_SET_ASIDES } from './constants';

export const formatPhoneInput = (value) => {
  const digits = String(value || '').replaceAll(/\D/g, '');
  const normalizedDigits = digits.length === 11 && digits.startsWith('1') ? digits.substring(1) : digits;
  const limitedDigits = normalizedDigits.substring(0, 10);

  if (limitedDigits.length <= 3) {
    return limitedDigits;
  }

  if (limitedDigits.length <= 6) {
    return `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3)}`;
  }

  return `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3, 6)}-${limitedDigits.substring(6)}`;
};

export const isValidEmail = (value) => {
  if (!value) {
    return false;
  }

  const trimmed = String(value).trim();
  const atIndex = trimmed.indexOf('@');

  if (atIndex <= 0 || atIndex !== trimmed.lastIndexOf('@') || trimmed.includes(' ')) {
    return false;
  }

  const domain = trimmed.slice(atIndex + 1);
  const dotIndex = domain.indexOf('.');

  return dotIndex > 0 && dotIndex < domain.length - 1;
};

export const isValidCompanyPhone = (value) => {
  if (!value) {
    return true;
  }

  const digits = String(value).replaceAll(/\D/g, '');
  const normalized = digits.length === 11 && digits.startsWith('1') ? digits.substring(1) : digits;

  return normalized.length === 10;
};

export const trimValue = (value) => String(value || '').trim();

export const emptyToNull = (value) => {
  const trimmedValue = trimValue(value);
  return trimmedValue || null;
};

export const getSelectedSetAsideObjects = (selectedCodes = []) => {
  return selectedCodes
    .map((code) => {
      const match = VENDORS_SET_ASIDES.find((item) => item.code === code);

      if (!match) {
        return null;
      }

      return {
        code: match.code,
        description: match.name,
      };
    })
    .filter(Boolean);
};

export const getSetAsideRenderValue = (selectedCodes = []) => {
  if (!selectedCodes.length) {
    return 'Select...';
  }

  return selectedCodes
    .map((code) => {
      const match = VENDORS_SET_ASIDES.find((item) => item.code === code);
      return match?.name || code;
    })
    .join(', ');
};

export const getCompanyName = (company = {}) => {
  return company.name || company.companyName || company.organizationName || '-';
};

export const getCompanyMeta = (company = {}) => {
  const values = [];

  if (company.ueiCode) {
    values.push(`UEI: ${company.ueiCode}`);
  }

  if (company.cageCode) {
    values.push(`Cage Code: ${company.cageCode}`);
  }

  if (company.companySize) {
    values.push(`Size: ${company.companySize}`);
  }

  return values.join('   ');
};

export const getVendorName = (vendor = {}) => {
  return vendor.name || vendor.companyName || vendor.organizationName || '-';
};

export const getVendorMeta = (vendor = {}) => {
  const values = [];

  if (vendor.ueiCode) {
    values.push(`UEI: ${vendor.ueiCode}`);
  }

  if (vendor.cageCode) {
    values.push(`Cage Code: ${vendor.cageCode}`);
  }

  if (vendor.primaryNaics) {
    values.push(`NAICS: ${vendor.primaryNaics}`);
  }

  if (vendor.city || vendor.state) {
    values.push([vendor.city, vendor.state].filter(Boolean).join(', '));
  }

  return values.join('   ');
};

export const getFederalAgencyName = (agency = {}) => {
  return agency.name || '-';
};

export const getFederalAgencyMeta = (agency = {}) => {
  const values = [];

  if (agency.type) {
    values.push(agency.type);
  }

  if (agency.city || agency.state) {
    values.push([agency.city, agency.state].filter(Boolean).join(', '));
  }

  return values.join('   ');
};

export const validateContactDetails = (values) => {
  const errors = {};

  if (!trimValue(values.firstName)) {
    errors.firstName = 'First Name is required';
  }

  if (!trimValue(values.lastName)) {
    errors.lastName = 'Last Name is required';
  }

  if (!trimValue(values.contactType)) {
    errors.contactType = 'Type is required';
  }

  if (!trimValue(values.origin)) {
    errors.origin = 'Origin is required';
  }

  if (values.origin === 'other' && !trimValue(values.originDetail)) {
    errors.originDetail = 'Please specify origin details';
  }

  if (!trimValue(values.email)) {
    errors.email = 'Email is required';
  } else if (!isValidEmail(trimValue(values.email))) {
    errors.email = 'Please enter a valid email address';
  }

  if (!trimValue(values.primaryPhone)) {
    errors.primaryPhone = 'Phone is required';
  }

  if (trimValue(values.secondaryEmail) && !isValidEmail(trimValue(values.secondaryEmail))) {
    errors.secondaryEmail = 'Please enter a valid email address';
  }

  return errors;
};

export const validateOrganizationDetails = (
  values,
  selectedCrmCompany,
  selectedRegisteredVendor,
  selectedFederalAgency
) => {
  const errors = {};

  if (values.organizationType === ORGANIZATION_TYPES.NEW && !trimValue(values.organizationName)) {
    errors.organizationName = 'Organization is required';
  }

  if (values.organizationType === ORGANIZATION_TYPES.CRM_COMPANY && !selectedCrmCompany?.id) {
    errors.crmCompany = values.contactType === 'agent' ? 'Please select a CRM agency' : 'Please select an organization';
  }

  if (values.organizationType === ORGANIZATION_TYPES.REGISTERED_VENDOR && !selectedRegisteredVendor?.id) {
    errors.registeredVendor = 'Please select a registered vendor';
  }

  if (values.organizationType === ORGANIZATION_TYPES.GOVERNMENT_AGENCY && !selectedFederalAgency?.id) {
    errors.federalAgency = 'Please select a federal agency';
  }

  if (trimValue(values.companyEmail) && !isValidEmail(trimValue(values.companyEmail))) {
    errors.companyEmail = 'Please enter a valid email address';
  }

  if (trimValue(values.companyPhone) && !isValidCompanyPhone(trimValue(values.companyPhone))) {
    errors.companyPhone = 'Please enter a valid 10-digit phone number';
  }

  return errors;
};
